import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { normalizeEmployeeLogin, useCurrentUser } from "@/lib/auth";
import { searchEmployees } from "@/lib/projectOverview";
import { canViewEmployeeTimeAccount } from "@/lib/zeiterfassungPermissions";

export interface EmployeeOption {
  login: string;
  display: string;
  isNachunternehmer?: boolean;
}

export function employeeLoginToOption(
  login: string,
  display?: string | null,
): AutocompleteOption<EmployeeOption> | null {
  const trimmed = login.trim();
  if (!trimmed) return null;
  const label = (display || trimmed).trim() || trimmed;
  return {
    key: trimmed.toLowerCase(),
    label,
    value: { login: normalizeEmployeeLogin(trimmed), display: label },
  };
}

interface EmployeeLockedAutocompleteProps {
  label?: string;
  placeholder?: string;
  value: AutocompleteOption<EmployeeOption> | null;
  onChange: (next: AutocompleteOption<EmployeeOption> | null) => void;
  className?: string;
  required?: boolean;
  error?: string;
}

export function EmployeeLockedAutocomplete({
  label = "Mitarbeiter",
  placeholder = "Mitarbeiter suchen…",
  value,
  onChange,
  className,
  required,
  error,
}: EmployeeLockedAutocompleteProps) {
  const { user } = useCurrentUser();
  return (
    <LockedAutocomplete<EmployeeOption>
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      minChars={0}
      required={required}
      error={error}
      className={className}
      loadOptions={async (q) => {
        const rows = await searchEmployees(q, 20);
        return rows
          .map(
            (row): AutocompleteOption<EmployeeOption> => ({
              key: row.id,
              label: row.label || row.name || row.id,
              description: row.subtitle,
              value: {
                login: normalizeEmployeeLogin(row.id),
                display: row.label || row.name || row.id,
                isNachunternehmer: Boolean(row.is_nachunternehmer),
              },
            }),
          )
          .filter((opt) => canViewEmployeeTimeAccount(user, opt.value.login));
      }}
    />
  );
}
